import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Check, Pencil, Plus, Trash2, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useUpsertIncidentStatusSummary } from 'components/IAP/hooks/incidentStatusSummaryHooks';
import {
  IncidentStatusSummaryDto,
  PerAgencyResources,
} from 'types/iapBaseTypes';

import { ICS204_TABLE } from '../../AssignmentList/assignmentCardStyles';
import AssignedResourcesEquipmentTypesDrawer from './AssignedResourcesEquipmentTypesDrawer';

/** Marks the blank row an Add click appends, which is editable on arrival. */
const APPENDED_ROW_ID = 'APPENDED_ROW_ID';

type Row = { id: string; value: PerAgencyResources };

/**
 * The record stores one flat row per agency-and-type pairing, while the table
 * reads as a grid of agencies against equipment columns.
 */
const mapAssignedResourcesFormFieldsToPayload = (
  assignedResources: PerAgencyResources[] = []
): IncidentStatusSummaryDto['assignedResources'] => {
  const payloadResources: IncidentStatusSummaryDto['assignedResources'] = [];

  assignedResources.forEach((agencyResource) => {
    const { agency, personnelCount, equipment } = agencyResource;
    payloadResources.push({
      agency,
      type: 'PERSONNEL',
      amount: personnelCount,
    });

    equipment.forEach((eq) => {
      payloadResources.push({
        agency,
        type: eq.type,
        amount: eq.quantity,
      });
    });
  });

  return payloadResources;
};

const toCount = (value: string): number => {
  const parsed = parseInt(value, 10);
  return Number.isFinite(parsed) ? parsed : 0;
};

const quantityOf = (resource: PerAgencyResources, type: string): string =>
  resource.equipment.find((eq) => eq.type === type)?.quantity ?? '0';

/**
 * Box 16, the resources each agency has committed.
 *
 * The equipment columns are not fixed: they are the union of the types any
 * agency has, edited together in a dialog so a new column arrives across every
 * row at once rather than leaving the grid ragged.
 */
const AssignedResourcesTable = ({
  statusSummaryId,
  originalAssignedResources,
  readOnly = false,
}: {
  statusSummaryId: string;
  originalAssignedResources: PerAgencyResources[];
  readOnly?: boolean;
}) => {
  const upsertIncidentStatusSummary =
    useUpsertIncidentStatusSummary(statusSummaryId);

  const nextId = useRef(0);
  const makeRows = useCallback(
    (items: PerAgencyResources[]): Row[] =>
      items.map((value) => ({ id: `row-${nextId.current++}`, value })),
    []
  );

  const [rows, setRows] = useState<Row[]>(() =>
    makeRows(originalAssignedResources ?? [])
  );
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState<PerAgencyResources | null>(null);
  const [equipmentDialogOpen, setEquipmentDialogOpen] = useState(false);

  // Take server state only between edits, so a refetch cannot overwrite a row
  // someone is part way through typing.
  const serialized = JSON.stringify(originalAssignedResources ?? []);
  useEffect(() => {
    if (editingId !== null) return;
    setRows((current) => {
      if (JSON.stringify(current.map((row) => row.value)) === serialized) {
        return current;
      }
      setSelectedIds([]);
      return makeRows(originalAssignedResources ?? []);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serialized, editingId]);

  const commitList = (nextRows: Row[]) => {
    setRows(nextRows);
    upsertIncidentStatusSummary({
      assignedResources: mapAssignedResourcesFormFieldsToPayload(
        nextRows.map((row) => row.value)
      ),
    });
  };

  const equipmentList = useMemo(() => {
    const types = new Set<string>();
    rows.forEach((row) =>
      row.value.equipment.forEach((eq) => {
        if (eq.type?.trim()) types.add(eq.type);
      })
    );
    return Array.from(types);
  }, [rows]);

  const visibleRows = useMemo(() => {
    const query = search.toLowerCase();
    const matches = rows.filter((row) =>
      row.value.agency.toLowerCase().includes(query)
    );
    // A blank row the user just added matches no search, and hiding the row
    // they are typing into would strand the edit.
    const editing = rows.find((row) => row.id === editingId);
    if (editing && !matches.includes(editing)) return [...matches, editing];
    return matches;
  }, [rows, search, editingId]);

  /** Summed across the rows on screen, as the printed form's last line is. */
  const totals = useMemo(() => {
    const personnel = visibleRows.reduce(
      (sum, row) => sum + toCount(row.value.personnelCount),
      0
    );
    const equipment = equipmentList.map((type) => ({
      type,
      quantity: visibleRows
        .reduce((sum, row) => sum + toCount(quantityOf(row.value, type)), 0)
        .toString(),
    }));
    return { personnel: personnel.toString(), equipment };
  }, [visibleRows, equipmentList]);

  const allSelected =
    visibleRows.length > 0 && selectedIds.length === visibleRows.length;

  const addAgency = () => {
    setRows((current) => [
      ...current,
      {
        id: APPENDED_ROW_ID,
        value: {
          agency: '',
          personnelCount: '0',
          equipment: equipmentList.map((type) => ({ type, quantity: '0' })),
        },
      },
    ]);
    setSelectedIds([]);
    setDraft({
      agency: '',
      personnelCount: '0',
      equipment: equipmentList.map((type) => ({ type, quantity: '0' })),
    });
    setEditingId(APPENDED_ROW_ID);
  };

  const saveEdit = () => {
    if (!draft) return;
    const edited = rows.map((row) =>
      row.id === editingId ? { ...row, value: draft } : row
    );
    // The appended row only earns a lasting id once it is committed.
    commitList(
      edited.map((row) =>
        row.id === APPENDED_ROW_ID
          ? { ...row, id: `row-${nextId.current++}` }
          : row
      )
    );
    setEditingId(null);
    setDraft(null);
  };

  const cancelEdit = () => {
    // Cancelling a row that was only just added drops it, rather than leaving a
    // blank agency behind that nothing would ever write.
    if (editingId === APPENDED_ROW_ID) {
      setRows((current) => current.filter((row) => row.id !== APPENDED_ROW_ID));
    }
    setEditingId(null);
    setDraft(null);
  };

  const deleteRows = (ids: string[]) => {
    commitList(rows.filter((row) => !ids.includes(row.id)));
    setSelectedIds([]);
  };

  /**
   * Applies the dialog's column list to every agency, keeping the quantities
   * already entered against types that survived and zeroing the new ones.
   */
  const handleSaveEquipmentTypes = (updatedEquipmentTypes: Set<string>) => {
    const types = Array.from(updatedEquipmentTypes);
    commitList(
      rows.map((row) => ({
        ...row,
        value: {
          ...row.value,
          equipment: types.map(
            (type) =>
              row.value.equipment.find((eq) => eq.type === type) ?? {
                type,
                quantity: '0',
              }
          ),
        },
      }))
    );
    setEquipmentDialogOpen(false);
  };

  const isLocked = editingId !== null;
  const columnCount = equipmentList.length + 4;

  const setDraftEquipment = (type: string, quantity: string) =>
    setDraft((current) =>
      current
        ? {
            ...current,
            equipment: current.equipment.some((eq) => eq.type === type)
              ? current.equipment.map((eq) =>
                  eq.type === type ? { ...eq, quantity } : eq
                )
              : [...current.equipment, { type, quantity }],
          }
        : current
    );

  return (
    <div className="tw:flex tw:min-w-0 tw:flex-col tw:gap-2">
      <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-2">
        <Input
          className="tw:max-w-xs"
          aria-label="Search assigned resources"
          placeholder="Search assigned resources..."
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
        {!readOnly && (
          <div className="tw:ml-auto tw:flex tw:gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={isLocked}
              onClick={addAgency}
            >
              <Plus className="tw:size-4" />
              Add Agency
            </Button>
            <Button
              type="button"
              size="sm"
              disabled={isLocked}
              onClick={() => setEquipmentDialogOpen(true)}
            >
              <Pencil className="tw:size-4" />
              Edit Equipment Types
            </Button>
          </div>
        )}
      </div>

      {!readOnly && selectedIds.length > 0 && (
        <div className="tw:flex tw:items-center tw:justify-between">
          <span className="tw:text-sm">{selectedIds.length} selected</span>
          <Button
            type="button"
            variant="destructive"
            size="sm"
            onClick={() => deleteRows(selectedIds)}
          >
            <Trash2 className="tw:size-4" />
            Delete Selected
          </Button>
        </div>
      )}

      <Table className={ICS204_TABLE} data-testid="ics209-assigned-resources">
        <TableHeader>
          <TableRow>
            <TableHead className="tw:w-10">
              <Checkbox
                aria-label="Select all agencies"
                checked={allSelected}
                disabled={visibleRows.length === 0 || isLocked}
                onCheckedChange={() =>
                  setSelectedIds(
                    allSelected ? [] : visibleRows.map((row) => row.id)
                  )
                }
              />
            </TableHead>
            <TableHead>Agency</TableHead>
            <TableHead>People</TableHead>
            {equipmentList.map((type) => (
              <TableHead key={type}>{type}</TableHead>
            ))}
            <TableHead className="tw:w-24" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {visibleRows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={columnCount} className="tw:text-center">
                {search
                  ? 'No results!'
                  : 'There are no agencies yet. Click Add Agency to add one.'}
              </TableCell>
            </TableRow>
          ) : (
            <>
              {visibleRows.map((row) => {
                const editing = row.id === editingId;
                const shown = editing && draft ? draft : row.value;
                return (
                  <TableRow key={row.id}>
                    <TableCell>
                      <Checkbox
                        aria-label={`Select ${row.value.agency || 'new agency'}`}
                        checked={selectedIds.includes(row.id)}
                        disabled={isLocked}
                        onCheckedChange={() =>
                          setSelectedIds((current) =>
                            current.includes(row.id)
                              ? current.filter((id) => id !== row.id)
                              : [...current, row.id]
                          )
                        }
                      />
                    </TableCell>
                    <TableCell>
                      {editing ? (
                        <Input
                          autoFocus
                          aria-label="Agency"
                          placeholder="Enter agency"
                          value={shown.agency}
                          onChange={(event) =>
                            setDraft((current) =>
                              current
                                ? { ...current, agency: event.target.value }
                                : current
                            )
                          }
                        />
                      ) : (
                        <span>{row.value.agency}</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {editing ? (
                        <Input
                          type="number"
                          aria-label="People"
                          placeholder="Enter number of people"
                          value={shown.personnelCount}
                          onChange={(event) =>
                            setDraft((current) =>
                              current
                                ? {
                                    ...current,
                                    personnelCount: event.target.value,
                                  }
                                : current
                            )
                          }
                        />
                      ) : (
                        <span>{row.value.personnelCount}</span>
                      )}
                    </TableCell>
                    {equipmentList.map((type) => (
                      <TableCell key={type}>
                        {editing ? (
                          <Input
                            type="number"
                            aria-label={`Quantity of ${type}`}
                            placeholder={`Qty of ${type}`}
                            value={quantityOf(shown, type)}
                            onChange={(event) =>
                              setDraftEquipment(type, event.target.value)
                            }
                          />
                        ) : (
                          <span>{quantityOf(row.value, type)}</span>
                        )}
                      </TableCell>
                    ))}
                    <TableCell>
                      <div className="tw:flex tw:justify-end tw:gap-1">
                        {editing ? (
                          <>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              aria-label="Save agency"
                              onClick={saveEdit}
                            >
                              <Check className="tw:size-4" />
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              aria-label="Cancel agency"
                              onClick={cancelEdit}
                            >
                              <X className="tw:size-4" />
                            </Button>
                          </>
                        ) : readOnly ? null : (
                          <>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              aria-label={`Edit ${row.value.agency}`}
                              disabled={isLocked}
                              onClick={() => {
                                setSelectedIds([]);
                                setDraft(row.value);
                                setEditingId(row.id);
                              }}
                            >
                              <Pencil className="tw:size-4" />
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              aria-label={`Delete ${row.value.agency}`}
                              disabled={isLocked}
                              onClick={() => deleteRows([row.id])}
                            >
                              <Trash2 className="tw:size-4" />
                            </Button>
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
              <TableRow data-testid="ics209-assigned-resources-total">
                <TableCell />
                <TableCell>Total</TableCell>
                <TableCell>{totals.personnel}</TableCell>
                {totals.equipment.map((eq) => (
                  <TableCell key={eq.type}>{eq.quantity}</TableCell>
                ))}
                <TableCell />
              </TableRow>
            </>
          )}
        </TableBody>
      </Table>

      <span className="tw:text-sm tw:text-muted-foreground">
        {visibleRows.length} agencies
      </span>

      {!readOnly && equipmentDialogOpen ? (
        <AssignedResourcesEquipmentTypesDrawer
          equipmentTypes={equipmentList}
          toggle={() => setEquipmentDialogOpen(false)}
          onSave={handleSaveEquipmentTypes}
        />
      ) : null}
    </div>
  );
};

export default AssignedResourcesTable;
